import { isLocalHost, VideoEntryPoint } from './constants/constants';
import { logger } from './services/logger/loggerService';
import PwWebSDKInstance, { tryCheckInitApp } from './services/pwWebSDKService';
import { PlayerParamsState } from './types/playerParams.type';

export const getImageUrlFromData = (b64Data: string, contentType = '', sliceSize = 512) => {
  const byteCharacters = atob(b64Data);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize);

    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  const blob = new Blob(byteArrays, { type: contentType });
  return URL.createObjectURL(blob);
};

export const displayDuration = (seconds: number) => {
  seconds = seconds || 0;
  const result: string[] = [];
  result.unshift(String(seconds % 60).padStart(2, '0'));
  seconds = parseInt(seconds / 60 + '');
  result.unshift(String(seconds % 60).padStart(2, '0'));
  seconds = parseInt(seconds / 60 + '');
  result.unshift(String(seconds % 24).padStart(2, '0'));
  seconds = parseInt(seconds / 24 + '');
  return result.join(':');
};

export function getRandomInt(min: number, max: number) {
  return parseInt(Math.random() * (max - min) + min + '');
}

export function decoding(textInput: any) {
  let decodedText = textInput;
  try {
    decodedText = decodeURIComponent(textInput);
  } catch (e) {
    logger.debug('Error in decoding', e);
    return '';
  }
  return decodedText;
}

export const capitalizeFirstLetter = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const sentryIgnoreErrorPattern = [
  '/node_modules/',
  'The play() request was interrupted',
  "play() failed because the user didn't interact",
  "The fetching process for the media resource was aborted by the user agent at the user's request.",
  'The request is not allowed by the user agent or the platform in the current context, possibly because the user denied permission.',
  'Invalid DOM property `%s`',
  'Cannot update a component',
  'Device Id is required',
  'Conviva: Status 0, will not retry',
  'Unauthorised Scope Access',
  'CODE:3 MEDIA_ERR_DECODE',
  'UnhandledRejection',
  /Failed to fetch/i,
  'Playback on other Websites has been disabled by the video owner', // ignoring sentry error where playback is disabled by video owner themselves. https://sentry-web.penpencil.co/organizations/physicswallah/issues/3652/?environment=prod&project=57&query=is%3Aunresolved%20issue.priority%3A%5Bhigh%2C%20medium%5D&referrer=issue-stream&stream_index=18
  'TypeError: Load failed', // ignoring conviva load error,
  'The play method is not allowed by the user agent or the platform in the current context',
  'play() can only be initiated by a user gesture',
  'ChunkLoadError', // ignoring as the default chunkloadtimeout https://webpack.js.org/configuration/output/#outputchunkloadtimeout is set to 2 seconds and in some browser it is timing out. New bundler should fix it.
  'The operation is not supported', // ignoring this as it is expected in certain case such as when the browser doesn't support a certain feature.
  'AbortError', // happening on safari when
  "Cannot read properties of undefined (reading 'el_')", // VideoJS internal error
  'is not a function', // Generic function call errors on player methods
  'Could not load https://modelvista.vercel.app/', // ignoring modelvista load error
  'can\'t access property "id", e is null', // VideoJS internal null reference error
  'Error creating WebGL context.', // WebGL not supported or disabled
  'NetworkError when attempting to fetch resource.', // Network fetch error
  "Failed to execute 'transaction' on 'IDBDatabase': The database connection is closing", // IndexedDB connection closing error
  '$.warn is not a function', // jQuery warn function not available
  "Cannot read properties of undefined (reading 'videoTimingInfo')", // VideoJS internal timing error
  /puppeteer/i, // Puppeteer related errors
  /network error/i, // Generic network errors
];

export const VERNACULAR_BATCH_DEFAULT_SELECTED_LANGUAGE = 'vernacularBatchDefaultLanguage';

export const getVernacularDefaultLanguage = () => {
  return localStorage.getItem(VERNACULAR_BATCH_DEFAULT_SELECTED_LANGUAGE) || '';
};

export const setVernacularDefaultLanguage = (language: string) => {
  localStorage.setItem(VERNACULAR_BATCH_DEFAULT_SELECTED_LANGUAGE, language);
};

export const convertHHMMSSTimeToSeconds = (time: string) => {
  const [hh = 0, mm = 0, ss = 0] = time.split(':').map(Number);
  return hh * 3600 + mm * 60 + ss;
};

export const getDurationInSeconds = (
  startTime: string,
  endTime: string,
  isLastIndex: boolean,
): number => {
  const toSeconds = (time: string) => {
    const [hh, mm, ss] = time.split(':').map(Number);
    return hh * 3600 + mm * 60 + ss;
  };

  const duration = toSeconds(endTime) - toSeconds(startTime);
  return isLastIndex ? duration : duration + 1; // Add 1 only if it's not the last index
};

export const isKhazanaPlaybackRequested = (params: PlayerParamsState) => {
  const { entryPoint, khazanaProgramId, contentId, batchSubjectId } = params;
  const isEntryPointKhazana =
    entryPoint && entryPoint.includes(VideoEntryPoint.KHAZANA_LECTURE_VIDEOS);
  const areAllRequiredParamsAvailable = khazanaProgramId && contentId && batchSubjectId;
  return Boolean(isEntryPointKhazana && areAllRequiredParamsAvailable);
};

export const isFeaturesEnabledInEmbededMode = (params: PlayerParamsState) => {
  const { isCommentsEnabled, isReportEnabled, isRatingEnabled } = params;
  const isKhazana = isKhazanaPlaybackRequested(params);
  const isAnyEmbeededFeatureEnabled = isCommentsEnabled || isReportEnabled || isRatingEnabled;
  return isAnyEmbeededFeatureEnabled || isKhazana;
};

export const updateDevTokenFromCred = () => {
  const isLocalhost = isLocalHost();
  if (!isLocalhost) return null;

  const isLoggedIn = PwWebSDKInstance?.isLoggedIn;

  if (!isLoggedIn) {
    tryCheckInitApp();
  }
};

export const handleServerTime = (time: number, requestStartTime: number) => {
  const latency = Math.round((Date.now() - requestStartTime) / 2);
  localStorage.setItem('server-time', time.toString());
  const serverDate = new Date(time);
  const localDate = new Date();
  const timeDifference = localDate.getTime() - serverDate.getTime() + latency;
  localStorage.setItem('time-difference', timeDifference.toString());
};
